import type { DealTransformed } from "@/lib/schema/deal-schema";
import { formatTimeAgo } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import type { FieldDef } from "./fields";

/** Display for a registry field that has no bespoke cell. */
export function GenericCell({
  field,
  deal,
}: {
  field: FieldDef;
  deal: DealTransformed;
}) {
  const value = field.accessor(deal);

  if (value == null || value === "") {
    return <span className="text-xs text-muted-foreground/40">—</span>;
  }

  if (field.type === "boolean") {
    return (
      <span className="text-xs text-muted-foreground/80">
        {value ? "Yes" : "No"}
      </span>
    );
  }

  if (field.type === "date" && value instanceof Date) {
    return (
      <span className="font-mono text-xs tabular-nums text-muted-foreground/60">
        {formatTimeAgo(value)}
      </span>
    );
  }

  if (field.type === "number" && typeof value === "number") {
    return (
      <span className="font-mono text-sm tabular-nums text-foreground">
        {Math.round(value)}
      </span>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-xs text-muted-foreground/40">—</span>;
    }
    return (
      <div className="flex flex-wrap items-center gap-1">
        {value.slice(0, 2).map((entry, i) => (
          <span
            key={i}
            className="inline-flex items-center rounded-sm bg-input px-1.5 py-0.5 text-xs text-foreground"
          >
            {String(entry)}
          </span>
        ))}
        {value.length > 2 && (
          <span className="text-xs text-muted-foreground/60">
            +{value.length - 2}
          </span>
        )}
      </div>
    );
  }

  return <TruncatedText value={String(value)} />;
}

/**
 * How many characters a cell shows before it is worth a tooltip.
 *
 * Every value is clamped to the column width regardless; this only decides
 * whether to mount a tooltip to recover the rest. Short values — "Yes", a
 * country, a business model — never overflow, and wrapping each of them in a
 * Radix tooltip would put one listener per cell on a table of hundreds.
 */
const TRUNCATION_THRESHOLD = 28;

/**
 * A text cell that cannot widen its column.
 *
 * Website URLs are the reason: a single long one set the column's intrinsic
 * width and squeezed the rest of the row, because the cell rendered the raw
 * string with nothing to clamp it. `truncate` needs a width to truncate
 * against, hence the explicit max — a table cell's default is content-sized,
 * so `max-w-full` would resolve to the very width we are trying to cap.
 */
function TruncatedText({ value }: { value: string }) {
  const clamped = (
    <span className="block max-w-[200px] truncate text-xs text-muted-foreground/80">
      {value}
    </span>
  );

  if (value.length <= TRUNCATION_THRESHOLD) return clamped;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{clamped}</TooltipTrigger>
        <TooltipContent>
          {/* `break-all` because the overflowing value is usually a URL, which
              has no spaces to wrap at. */}
          <p className="max-w-[320px] break-all">{value}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
